import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import swal from 'sweetalert';
import { Modal } from 'react-bootstrap';
import { createProvider, updateProvider } from '../../api-services';
import { useFormik } from 'formik';
import { string_required, email } from '../../validation/validationSchema';
import * as Yup from 'yup';
const initialState = {
  provider_name: '',
  type: 'CALL',
  add_prefix: '',
  remove_prefix: '',
  outbound_ip_host: '',
  inbound_ip_host: '',
  mode: 'HTTP',
  allow_codecs: '',
};

const AddProviderModal = ({
  setOpenModal,
  openModal,
  editData = {},
  setEditData,
}) => {
  const queryClient = useQueryClient();
  const isEdit = editData && Object.keys(editData).length > 0;
  const [initialValues, setInitialValues] = useState(initialState);

  const { isLoading, mutateAsync } = useMutation(
    'createProvider',
    createProvider,
    {
      onSuccess: (data) => {
        swal({
          text: data?.data?.message ?? 'Success!',
          icon: 'success',
          type: 'success',
          timer: 3000,
        }).then(() => {
          queryClient.invalidateQueries('providers');
          handleClose();
        });
      },
      onError: (error) => {
        swal({
          type: 'danger',
          text: error?.response?.data?.message,
          icon: 'error',
          timer: 3000,
        });
      },
    },
  );

  const { isLoading: isLoadingUpdate, mutateAsync: mutateAsyncUpdate } =
    useMutation('updateProvider', (payload) => updateProvider(payload), {
      onSuccess: (data) => {
        swal({
          text: data?.data?.message,
          icon: 'success',
          type: 'success',
          timer: 2000,
        }).then(() => {
          queryClient.invalidateQueries('providers');
          handleClose();
        });
      },
      onError: (error) => {
        swal({
          title: error?.response?.data?.message,
          type: 'danger',
          icon: 'error',
        });
      },
    });

  useEffect(() => {
    if (editData && Object.keys(editData).length > 0) {
      const {
        name,
        type,
        add_prefix,
        remove_prefix,
        host_ip_outbound,
        host_ip_inbound,
        request_mode,
        allow_codecs,
        uniquecode,
      } = editData;
      const valuesToSet = {
        provider_name: name,
        type: type,
        add_prefix: add_prefix,
        remove_prefix: remove_prefix,
        outbound_ip_host: host_ip_outbound,
        inbound_ip_host: host_ip_inbound,
        mode: request_mode,
        allow_codecs: allow_codecs,
        uniquecode,
      };
      setInitialValues(valuesToSet);
    } else {
      setInitialValues(initialState);
    }
  }, [editData]);

  const validationSchema = Yup.object({
    provider_name: string_required,
  });

  const handleSubmitForm = async (values) => {
    const {
      provider_name,
      type,
      add_prefix,
      remove_prefix,
      outbound_ip_host,
      inbound_ip_host,
      mode,
      allow_codecs,
    } = values;
    const payload = {
      name: provider_name,
      type: type,
      add_prefix: add_prefix,
      remove_prefix: remove_prefix,
      host_ip_outbound: outbound_ip_host,
      host_ip_inbound: inbound_ip_host,
      request_mode: mode,
      allow_codecs: allow_codecs,
    };
    if (isEdit) {
      payload.uniquecode = values?.uniquecode;
      await mutateAsyncUpdate(payload);
    } else {
      await mutateAsync(payload);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmitForm,
  });

  const handleClose = () => {
    formik.resetForm();
    setEditData({});
    setOpenModal(false);
  };

  return (
    <Modal
      size="md"
      centered
      show={openModal}
      onHide={() => handleClose()}
      backdrop="static"
      keyboard={false}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="modal-header ">
          <div className="icon_arrow"></div>
          <h5 className="modal-title heading heading--xs mb-0 ">
            {isEdit ? 'Update Provider' : 'Add Provider'}
          </h5>
        </div>

        <div className="modal-body">
          <div className="form-group input_control input_control--gap">
            <div className="w-100">
              <input
                type="text"
                className="form-control"
                placeholder="Provider Name"
                name="provider_name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.provider_name}
              />
              {formik.touched.provider_name && formik.errors.provider_name ? (
                <div className="error_required">
                  {formik.errors.provider_name}
                </div>
              ) : null}
            </div>
          </div>

          <div className="form-group input_control">
            <div className="w-100">
              <select
                className="form-control"
                name="type"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.type}
              >
                <option value="CALL">CALL</option>
                <option value="SMS">SMS</option>
              </select>
            </div>
          </div>
          <div className="form-group input_control input_control--gap">
            <div className="w-100">
              <input
                type="text"
                className="form-control"
                placeholder="Add Prifix"
                name="add_prefix"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.add_prefix}
              />
            </div>
          </div>
          <div className="form-group input_control input_control--gap">
            <div className="w-100">
              <input
                type="text"
                className="form-control"
                placeholder="Remove Prifix"
                name="remove_prefix"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.remove_prefix}
              />
            </div>
          </div>
          <div className="form-group input_control input_control--gap">
            <div className="w-100">
              <input
                type="text"
                className="form-control"
                placeholder="Outbound IP Host"
                name="outbound_ip_host"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.outbound_ip_host}
              />
            </div>
          </div>
          <div className="form-group input_control input_control--gap">
            <div className="w-100">
              <input
                type="text"
                className="form-control"
                placeholder="Inbound IP Host"
                name="inbound_ip_host"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.inbound_ip_host}
              />
            </div>
          </div>

          <div className="form-group input_control">
            <div className="w-100">
              <select
                className="form-control"
                name="mode"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.mode}
              >
                <option value="HTTP">HTTP</option>
                <option value="SMPP">SMPP</option>
              </select>
            </div>
          </div>

          <div className="form-group input_control input_control--gap">
            <div className="w-100">
              <input
                type="text"
                className="form-control"
                placeholder="Allow Codecs"
                name="allow_codecs"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.allow_codecs}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => handleClose()}
          >
            Close
          </button>

          <button
            type="submit"
            disabled={isLoading || isLoadingUpdate}
            className="btn btn-dark-blue"
          >
            {isLoading || isLoadingUpdate ? 'Please Wait...' : 'Submit'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddProviderModal;
